import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { supabase } from "@/integrations/supabase/client";

const Auth: React.FC = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [organizationMode, setOrganizationMode] = useState<"create" | "join" | "later">("later");
  const [newOrgName, setNewOrgName] = useState("");
  const [joinOrgId, setJoinOrgId] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [showEmailVerification, setShowEmailVerification] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const { login, signUp, isAuthenticated, resendVerificationEmail } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Check URL params to determine initial tab
  const searchParams = new URLSearchParams(location.search);
  const initialTab = searchParams.get('tab') === 'signup' ? 'signup' : 'login';

  // No longer need to fetch organizations for signup since users will create or join by ID
  // Remove the organizations query

  useEffect(() => {
    if (isAuthenticated) {
      const from = (location.state as any)?.from?.pathname || "/admin";
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, location]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");
    setShowEmailVerification(false);

    const result = await login(email, password);
    if (result.error) {
      if (result.emailNotConfirmed) {
        setShowEmailVerification(true);
        setError("");
      } else {
        setError(result.error);
      }
    }
    setIsLoading(false);
  };

  const handleResendVerification = async () => {
    setIsLoading(true);
    setError("");
    setResendSuccess(false);

    const result = await resendVerificationEmail(email);
    if (result.error) {
      setError(result.error);
    } else {
      setResendSuccess(true);
    }
    setIsLoading(false);
  };

  const handleBackToLogin = () => {
    setShowEmailVerification(false);
    setResendSuccess(false);
    setError("");
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");

    // Validate organization data if not choosing "later"
    if (organizationMode === "create" && !newOrgName.trim()) {
      setError("Please enter an organization name.");
      setIsLoading(false);
      return;
    }

    if (organizationMode === "join" && !joinOrgId.trim()) {
      setError("Please enter an organization ID.");
      setIsLoading(false);
      return;
    }

    let orgData;
    if (organizationMode === "create") {
      orgData = { name: newOrgName.trim() };
    } else if (organizationMode === "join") {
      orgData = { id: joinOrgId.trim() };
    } else {
      orgData = undefined;
    }

    const result = await signUp(email, password, name, orgData);
    if (result.error) {
      setError(result.error);
    } else {
      setError("Check your email for a confirmation link!");
    }
    setIsLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl font-bold">ZooCertica</CardTitle>
          <CardDescription>
            Accreditation Management Platform
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue={initialTab} className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="login">Login</TabsTrigger>
              <TabsTrigger value="signup">Sign Up</TabsTrigger>
            </TabsList>
            
            <TabsContent value="login">
              {showEmailVerification ? (
                <div className="space-y-4">
                  <Alert>
                    <AlertDescription>
                      <div className="space-y-3">
                        <p className="font-semibold">Email Verification Required</p>
                        <p>
                          Your email address hasn't been verified yet. Please check your email inbox for a verification link.
                        </p>
                        {resendSuccess ? (
                          <p className="text-green-600 dark:text-green-400">
                            ✓ Verification email sent! Please check your inbox and spam folder.
                          </p>
                        ) : (
                          <p className="text-muted-foreground text-sm">
                            Didn't receive the email? Check your spam folder or click below to resend.
                          </p>
                        )}
                      </div>
                    </AlertDescription>
                  </Alert>
                  {error && (
                    <Alert variant="destructive">
                      <AlertDescription>{error}</AlertDescription>
                    </Alert>
                  )}
                  <div className="space-y-2">
                    <Button
                      onClick={handleResendVerification}
                      className="w-full"
                      disabled={isLoading || resendSuccess}
                    >
                      {isLoading ? "Sending..." : resendSuccess ? "Email Sent!" : "Resend Verification Email"}
                    </Button>
                    <Button
                      onClick={handleBackToLogin}
                      variant="outline"
                      className="w-full"
                      disabled={isLoading}
                    >
                      Back to Login
                    </Button>
                  </div>
                  <div className="text-sm text-muted-foreground text-center">
                    <p>After verifying your email, click "Back to Login" to sign in.</p>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      disabled={isLoading}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="password">Password</Label>
                    <Input
                      id="password"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      disabled={isLoading}
                    />
                  </div>
                  {error && (
                    <Alert variant="destructive">
                      <AlertDescription>{error}</AlertDescription>
                    </Alert>
                  )}
                  <Button type="submit" className="w-full" disabled={isLoading}>
                    {isLoading ? "Signing in..." : "Sign In"}
                  </Button>
                  <div className="text-center">
                    <Button asChild variant="link" className="text-sm p-0 h-auto">
                      <Link to="/forgot-password">Forgot your password?</Link>
                    </Button>
                  </div>
                </form>
              )}
            </TabsContent>
            
            <TabsContent value="signup">
              <form onSubmit={handleSignUp} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="signup-name">Name</Label>
                  <Input
                    id="signup-name"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    disabled={isLoading}
                  />
                </div>
                <div className="space-y-4">
                  <Label>Organization Setup (Optional)</Label>
                  <div className="space-y-3">
                    <div className="flex items-center space-x-2">
                      <input
                        type="radio"
                        id="create-org"
                        name="org-mode"
                        checked={organizationMode === "create"}
                        onChange={() => setOrganizationMode("create")}
                        className="h-4 w-4 text-primary"
                      />
                      <Label htmlFor="create-org" className="text-sm font-normal">
                        Create a new organization
                      </Label>
                    </div>
                    
                     {organizationMode === "create" && (
                      <Input
                        placeholder="Enter organization name"
                        value={newOrgName}
                        onChange={(e) => setNewOrgName(e.target.value)}
                        disabled={isLoading}
                        className="ml-6"
                        required
                        minLength={2}
                      />
                    )}

                    <div className="flex items-center space-x-2">
                      <input
                        type="radio"
                        id="join-org"
                        name="org-mode"
                        checked={organizationMode === "join"}
                        onChange={() => setOrganizationMode("join")}
                        className="h-4 w-4 text-primary"
                      />
                      <Label htmlFor="join-org" className="text-sm font-normal">
                        Join an existing organization
                      </Label>
                    </div>
                    
                     {organizationMode === "join" && (
                      <Input
                        placeholder="Enter organization ID"
                        value={joinOrgId}
                        onChange={(e) => setJoinOrgId(e.target.value)}
                        disabled={isLoading}
                        className="ml-6"
                        required
                      />
                    )}

                    <div className="flex items-center space-x-2">
                      <input
                        type="radio"
                        id="later-org"
                        name="org-mode"
                        checked={organizationMode === "later"}
                        onChange={() => setOrganizationMode("later")}
                        className="h-4 w-4 text-primary"
                      />
                      <Label htmlFor="later-org" className="text-sm font-normal">
                        I'll do this later
                      </Label>
                    </div>

                  </div>
                  <p className="text-sm text-muted-foreground">
                    {organizationMode === "later" 
                      ? "You can create or join an organization after signing up"
                      : "Organization admins can provide you with the organization ID to join their organization"
                    }
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="signup-email">Email</Label>
                  <Input
                    id="signup-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    disabled={isLoading}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="signup-password">Password</Label>
                  <Input
                    id="signup-password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    disabled={isLoading}
                  minLength={6}
                  />
                </div>
                
                <div className="flex items-start space-x-2">
                  <input
                    type="checkbox"
                    id="terms-agreement"
                    checked={agreedToTerms}
                    onChange={(e) => setAgreedToTerms(e.target.checked)}
                    className="mt-1 h-4 w-4 rounded border-gray-300"
                    disabled={isLoading}
                  />
                  <Label htmlFor="terms-agreement" className="text-sm font-normal leading-tight">
                    I agree to the{" "}
                    <a
                      href="/terms"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline"
                    >
                      Terms of Service
                    </a>
                    ,{" "}
                    <a
                      href="/privacy"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline"
                    >
                      Privacy Policy
                    </a>
                    , and{" "}
                    <a
                      href="/sla"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline"
                    >
                      SLA
                    </a>
                  </Label>
                </div>

                {error && (
                  <Alert variant={error.includes("Check your email") ? "default" : "destructive"}>
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}
                <Button type="submit" className="w-full" disabled={isLoading || !agreedToTerms}>
                  {isLoading ? "Creating account..." : "Create Account"}
                </Button>
              </form>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};

export default Auth;